import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ClaimService, Icons, WorkflowMonitorService } from '@lsq-frontend/common-ui';
import { ManageSupplierService } from '../manage-supplier.service';
import {
    GetSupplierInformationResponse,
    GetSupplierInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/get-supplier-information';
import { combineLatest, Observable, Subject } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import { UpdateSupplierInformationService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/update-supplier-information';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { CurrencyCode } from '@lsq-frontend/remote-services/all-java/common-mk0';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
@LifecycleAware()
@Component({
    selector: 'manage-supplier-information',
    styleUrls: ['./manage-supplier-information.component.scss'],
    template: `
        <div class="manage-supplier-information row">
            <div class="col-12 col-lg-8" [style.marginBottom]="'2rem'">
                <card [headerTitle]="'Supplier Information'" [style.marginBottom]="'3rem'">
                    <div card-body *ngIf="!initialized" class="implementation-card-body">
                        <lsq-circle-loader></lsq-circle-loader>
                    </div>
                    <div *ngIf="initialized" card-body class="information-card-body">
                        <div class="information-controls">
                            <form
                                class="control-inputs"
                                [formGroup]="formGroup"
                                (ngSubmit)="saveChanges()"
                            >
                                <div class="row">
                                    <div class="col-6">
                                        <lsq-label
                                            label="Supplier ID*"
                                            subLabel="Number used to reference the Supplier in SFTP files."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierId"
                                            formControlName="supplierId"
                                            [group]="formGroup"
                                            [disabled]="
                                                !this.canEditSuppliers ||
                                                !this.canEditSftpInformation
                                            "
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-6">
                                        <lsq-label
                                            label="Tax ID"
                                            subLabel="EIN or Tax ID of the supplier as defined by the IRS."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="taxId"
                                            formControlName="taxId"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-label
                                            label="Supplier Name*"
                                            subLabel="The legal name of the supplier."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierName"
                                            formControlName="supplierName"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-label
                                            label="DBAs"
                                            subLabel="The name the supplier does business as."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierDba"
                                            formControlName="supplierDba"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-label
                                            label="Address*"
                                            subLabel="The postal address of the supplier."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierAddress"
                                            formControlName="supplierAddress"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-label label="Address Line 2"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierAddressLine2"
                                            formControlName="supplierAddressLine2"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4">
                                        <lsq-label label="City / Locality*"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierCity"
                                            formControlName="supplierCity"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-4">
                                        <lsq-label label="State / Province*"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierState"
                                            formControlName="supplierState"
                                            [group]="formGroup"
                                        ></lsq-input>
                                    </div>
                                    <div class="col-4">
                                        <lsq-label label="Postal Code*"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierPostalCode"
                                            formControlName="supplierPostalCode"
                                            [group]="formGroup"
                                            [maxLength]="10"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-6">
                                        <lsq-label label="Country / Region*"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierCountry"
                                            formControlName="supplierCountry"
                                            [group]="formGroup"
                                        ></lsq-input>
                                    </div>
                                    <div class="col-6">
                                        <lsq-label label="Currency*"></lsq-label>
                                        <lsq-select
                                            [basicLabel]="true"
                                            name="supplierCurrencyCode"
                                            [options]="currency"
                                            formControlName="supplierCurrencyCode"
                                            [group]="formGroup"
                                        ></lsq-select>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-6">
                                        <lsq-label label="Phone Number*"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            type="tel"
                                            name="supplierPhoneNumber"
                                            formControlName="supplierPhoneNumber"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-6">
                                        <lsq-label label="Website"></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="supplierWebsite"
                                            formControlName="supplierWebsite"
                                            [group]="formGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row" *ngIf="this.canEditSuppliers">
                                    <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                        <lsq-button
                                            [style.marginRight]="'2rem'"
                                            appearance="secondary"
                                            size="block"
                                            [disabled]="
                                                !this.canEditSuppliers ||
                                                !(this.hasChanges$ | async)
                                            "
                                            (buttonClick)="resetForm()"
                                        >
                                            Clear Changes
                                        </lsq-button>
                                    </div>
                                    <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                        <lsq-button
                                            appearance="primary"
                                            size="block"
                                            type="submit"
                                            [submitting]="submitting"
                                            [disabled]="
                                                !canEditSuppliers || !(this.hasChanges$ | async)
                                            "
                                        >
                                            Save
                                        </lsq-button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </card>
            </div>
            <div class="col-12 col-lg-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">
                        Additional Information
                    </p>
                    <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                        Please enter your updated information for the Supplier and once you are done
                        click the Save button.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
export class ManageSupplierInformationComponent implements OnInit, OnDestroy {
    public currency = Object.keys(CurrencyCode).map((currencyCode) => ({
        label: currencyCode,
        value: currencyCode
    }));

    public canEditSuppliers = this._claimService.isOnSync(DashboardClaim.AdminEditSupplier);
    public canEditSftpInformation = false;
    public icons = Icons;

    public initialized = false;
    public submitting = false;
    private _cachedResponse: GetSupplierInformationResponse;

    private _refetchSubject = new Subject();
    constructor(
        private _claimService: ClaimService,
        private _manageSupplierService: ManageSupplierService,
        private _getSupplierInformationService: GetSupplierInformationService,
        private _updateSupplierInformationService: UpdateSupplierInformationService,
        private _alertService: AlertService,
        private _workflowMonitorService: WorkflowMonitorService
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageSupplierService.relationshipKey$,
            this._refetchSubject.pipe(startWith(true))
        ])
            .pipe(forLifeOf(this))
            .subscribe(async ([relationshipKey, _]) => {
                this.initialized = false;
                this._cachedResponse = await this._getSupplierInformationService.executeOnce({
                    relationshipKey
                });
                this.canEditSftpInformation = this._cachedResponse.sftpInformation.editable;
                this.resetForm();
                this.initialized = true;
            });
    }

    public formGroup = new FormGroup({
        supplierId: new FormControl({ value: null }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        taxId: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            LsqValidators.exactLength(9),
            LsqValidators.number
        ]),
        supplierName: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        supplierDba: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            LsqValidators.maxLength(255)
        ]),
        supplierAddress: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        supplierAddressLine2: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            LsqValidators.maxLength(255)
        ]),
        supplierCity: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        supplierState: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        supplierPostalCode: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.minLength(5),
            LsqValidators.maxLength(255)
        ]),
        supplierCountry: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required
        ]),
        supplierCurrencyCode: new FormControl({ value: 'USD', disabled: true }, [
            Validators.required
        ]),
        supplierPhoneNumber: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        supplierWebsite: new FormControl({ value: null, disabled: !this.canEditSuppliers }, [
            LsqValidators.maxLength(255)
        ])
    });

    public hasChanges$: Observable<boolean> = this.formGroup.valueChanges.pipe(
        map(() => {
            this._workflowMonitorService.markWorkflowStarted();
            return this.formGroup.dirty;
        }),
        startWith(false),
        shareReplay(1)
    );

    public async saveChanges(): Promise<void> {
        this.formGroup.markAllAsTouched();
        if (this.formGroup.valid) {
            try {
                this.submitting = true;
                const relationshipKey = await this._manageSupplierService.relationshipKey$
                    .pipe(first())
                    .toPromise();

                await this._updateSupplierInformationService.executeOnce({
                    ...this.formGroup.value,
                    relationshipKey
                });

                window.scrollTo({ top: 0, behavior: 'smooth' });

                this._alertService.success('Supplier information successfully updated.');
                this._refetchSubject.next();
            } catch (e) {
                this._alertService.error('An error ocurred while updating.');
            }
            this.submitting = false;
        }
    }

    public resetForm(): void {
        this.formGroup.reset({
            ...this._cachedResponse,
            supplierId: this._cachedResponse.sftpInformation.supplierId,
            supplierPostalCode:
                this._cachedResponse.supplierPostalCode || this._cachedResponse.supplierZipCode,
            supplierCurrencyCode: 'USD'
        });
        this._workflowMonitorService.markWorkflowFinished();
    }

    public ngOnDestroy(): void {
        this._workflowMonitorService.markWorkflowFinished();
    }
}
